import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Loader2, Mail, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useApp } from "@/context/AppContext";
import { api, errMsg } from "@/lib/api";
import { pdfBlob, xlsxBlob } from "@/lib/exporters";
import { fmtDate } from "@/lib/format";

export const DIALOG_DEFAULTS = {
  title: "Email Annual Summary {year}", description: "The General Council office gets an email with secure download buttons for the report (links work for {days} days).",
  to_label: "To:", note_placeholder: "Short note (optional), e.g. Please find our {year} annual report...", pdf_label: "PDF report",
  excel_label: "Excel spreadsheet", last_sent: "Last sent: {last_year} report on {date} by {by}", send_button: "Send to General Council",
};

export function dialogText(tx, k, _f, vals) {
  const p = tx?.[k];
  if (p && !p.on) return "";
  return Object.entries(vals).reduce((s, [a, b]) => s.split(`{${a}}`).join(b ?? ""), p?.text || DIALOG_DEFAULTS[k]);
}

export function GcEmailEdit({ value, onSaved, onCancel, testid = "annual-email-to" }) {
  const [email, setEmail] = useState(value || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setEmail(value || ""); }, [value]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put("/settings", { annual: { gc_email: email.trim() } }); toast.success(email.trim() ? "General Council office email saved" : "General Council office email removed"); await onSaved?.(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form className="space-y-2" onSubmit={save} data-testid={`${testid}-form`}>
      <label className="text-sm font-semibold text-slate-900" htmlFor={`${testid}-input`}>General Council office email</label>
      <div className="flex gap-2">
        <Input id={`${testid}-input`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="office@example.org" className="h-10 bg-white" data-testid={`${testid}-input`} />
        <Button type="submit" disabled={busy || email.trim() === (value || "")} className="h-10" data-testid={`${testid}-save`}>Save</Button>
        {onCancel && <Button type="button" variant="outline" className="h-10" onClick={onCancel} data-testid={`${testid}-cancel`}>Cancel</Button>}
      </div>
      <p className="text-xs text-slate-500">Clear the box and press Save to remove the address.</p>
    </form>
  );
}

export function EmailAnnualDialog({ open, onClose, year, title, subtitle, sections, sig }) {
  const { settings, reload, hasArea } = useApp();
  const to = settings.annual?.gc_email;
  const canEdit = hasArea("settings");
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState("");
  const [excel, setExcel] = useState(true);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [tx, setTx] = useState(null);
  useEffect(() => { if (open) { setNote(""); setEditing(false); api.get("/reports/annual/emails").then((r) => setHistory(r.data)).catch(() => {}); api.get("/email-text/annual_dialog/view").then((r) => setTx(r.data.texts)).catch(() => {}); } }, [open]);
  const last = history[0];
  const w = (k, fallback) => dialogText(tx, k, fallback, { year, days: 30, last_year: last?.year, date: last ? fmtDate(last.sent_at) : "", by: last?.sent_by_name });

  const send = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("year", year);
      fd.append("note", note);
      fd.append("pdf", pdfBlob(title, subtitle, sections, sig), `MWJK-Annual-Summary-${year}.pdf`);
      if (excel) fd.append("xlsx", xlsxBlob(sections, sig), `MWJK-Annual-Summary-${year}.xlsx`);
      await api.post("/reports/annual/email", fd);
      toast.success(`Annual Summary ${year} emailed to ${to}`);
      onClose();
    } catch (e) { toast.error(errMsg(e), { id: "annual-email-error" }); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="annual-email-dialog">
        <DialogHeader>
          <DialogTitle className={w("title") ? "" : "sr-only"} data-testid="annual-email-title">{w("title") || `Email Annual Summary ${year}`}</DialogTitle>
          <DialogDescription className={w("description") ? "" : "sr-only"} data-testid="annual-email-description">{w("description")}</DialogDescription>
        </DialogHeader>
        {editing || (!to && canEdit) ? (
          <GcEmailEdit value={to} onCancel={to ? () => setEditing(false) : null} onSaved={async () => { await reload(); setEditing(false); }} />
        ) : to ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 p-3 text-sm"><span><span className="font-semibold" data-testid="annual-email-to-label">{w("to_label")}</span> <span data-testid="annual-email-to">{to}</span></span>
              {canEdit && <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid="annual-email-to-change"><Pencil className="mr-1 h-3.5 w-3.5" /> Change</Button>}</div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} rows={4} placeholder={w("note_placeholder")} data-testid="annual-email-note" />
            <div className="space-y-2 text-sm">
              <label className="flex items-center gap-2"><Checkbox checked disabled /> <span data-testid="annual-email-pdf-label">{w("pdf_label")}</span></label>
              <label className="flex items-center gap-2"><Checkbox checked={excel} onCheckedChange={(v) => setExcel(!!v)} data-testid="annual-email-excel" /> <span data-testid="annual-email-excel-label">{w("excel_label")}</span></label>
            </div>
            {last && w("last_sent") && <p className="text-xs text-slate-500" data-testid="annual-email-last">{w("last_sent")}</p>}
            <Button onClick={send} disabled={busy} className="w-full" data-testid="annual-email-send">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} {w("send_button")}
            </Button>
          </div>
        ) : (
          <p className="rounded-lg bg-amber-50 p-4 text-sm text-amber-900" data-testid="annual-email-missing">
            No General Council office email is saved yet. Add it in <Link to="/admin" className="font-semibold underline">Admin Panel → Toggles & Permissions</Link>.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
